// src/ThemedApp.js
import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { CompanyProvider, useCompany } from './CompanyContext';
import { useTheme } from './ThemeContext';
import { TutorialProvider, TutorialChecklist } from './components/tutorial';
import Login from './components/auth/Login.js';
import Register from './components/auth/Register.js';
import Overview from './components/Overview';
import { EmailVerificationRestrictionProvider } from './components/email-verification/EmailVerificationRestrictionContext';
import ArchivesPage from './components/Archives';
import SettingsWrapper from './components/settings/SettingsWrapper';
import { useAuth } from './context/AuthContext';
import ResponsiveEmployeeManagement from './company-management/ResponsiveEmployeeManagement';
import InviteEmployeesPage from './company-management/invitations/pages/InviteEmployeesPage';
import { PERMISSIONS } from './company-management/constants/permissions';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from './company-management/hooks/usePermissions';
import ClientProfilePage from './client-profiles/pages/ClientProfilePage';
import { UpcomingAppointments } from './components/Overview';
import TeamLabelCalendarPage from './calendar_v3/pages/TeamLabelCalendarPage';
import ServiceDetailsPage from './components/services/ServiceDetailsPage';
import PackageDetailsPage from './components/services/packages-v2/PackageDetailsPage';
import AddOnDetailsPage from './components/services/addons-v2/details-page/AddOnDetailsPage';
import ServiceEditPage from './components/services/service-details-v2/ServiceEditPage';
import ServiceCreatePage from './components/services/service-details-v2/ServiceCreatePage';
import { LegacyLocationEditRedirect } from './locations/settings/LocationEditPage';
import MeetingCreatePage from './meetings/pages/MeetingCreatePage';
import MeetingsPage from './meetings/pages/MeetingsPage';
import MeetingsOverview from './components/overview/MeetingsOverview';
import OverviewMobileRouteShell from './components/overview/mobile-bottom-nav/OverviewMobileRouteShell';
import MeetingDetailsPage from './meetings/pages/MeetingDetailsPage';
import EmployeeProfileRoute from './company-management/components/EmployeeProfileRoute';
import AppointmentsView from './appointments/hour-slot-view/AppointmentsView';
import ResponsiveClientManagement from './components/clients/ResponsiveClientManagement';
import SupportPage from './components/support/SupportPage';
import SupportTicketsPage from './features/support-tickets/pages/SupportTicketsPage';
import SupportTicketDetailPage from './features/support-tickets/pages/SupportTicketDetailPage';
import ManualBookingPage from './manual-booking/components/booking/ManualBookingPage';
import AppointmentsPage from './components/appointments/AppointmentsPage';
import ManualBookingSuccessPage from './manual-booking/pages/ManualBookingSuccessPage';
import AppointmentDetailsPage from './manual-booking/appointment/AppointmentDetailsPage';
import StaffAppointmentDetailsPage from './components/appointments/AppointmentDetailsPage';
import PermissionsManager from './company-management/components/permissions/PermissionsManager';
import Unauthorized from './components/Unauthorized';
import { usePackageValidation } from './packages/hooks/usePackageValidation';
import { useImpersonation } from './admin-panel/impersonation/ImpersonationContext';
import { isImpersonationSessionActive, isImpersonationExiting, isImpersonationEntering, getImpersonationTargetLabel } from './impersonation/utils/impersonationSession';
import { isOverviewMobileNavPath } from './components/overview/mobile-bottom-nav/overviewMobileNavPaths';
import './impersonation/components/ImpersonationBanner.css';
import WorkspaceLoadingScreen from './components/loading/workspace/WorkspaceLoadingScreen';
import DevToolsPage from './dev-tools/pages/DevToolsPage';
import SpecialAdminRoute from './dev-tools/routes/SpecialAdminRoute';
// Auth/maintenance loading is enforced by App.js PrivateRoute. Package redirects stay
// here (live subscription updates) without a second loading spinner.
const ProtectedRoute = ({ children, requiredPermissions = [] }) => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { hasPackage, status, isLoading: packageLoading } = usePackageValidation(user?.companyId, !!user, {
    blockWhileLoading: false,
  });

  if (isImpersonationExiting() || isImpersonationEntering()) {
    return (
      <WorkspaceLoadingScreen
        message={isImpersonationExiting() ? 'Returning to admin panel...' : 'Signing into this account...'}
      />
    );
  }

  if (!user) {
    return <Navigate to="/app/login" replace />;
  }

  // PrivateRoute already blocks invalid packages on entry; here we only react once
  // validation has finished so a refresh does not flash a false package-expired redirect.
  if (!packageLoading && status !== null) {
    if (hasPackage) {
      if (status === 'trial' || status === 'grace_period' || status === 'cancelled') {
        // fall through to permission checks
      } else if (status === 'expired') {
        return <Navigate to="/app/package-expired" replace />;
      } else if (!['active', 'trial', 'cancelled', 'grace_period'].includes(status)) {
        return <Navigate to="/app/package-expired" replace />;
      }
    }
  }

  // Check permissions if any are required
  if (requiredPermissions.length > 0) {
    const hasAccess = requiredPermissions.every(permission => hasPermission(permission));
    if (!hasAccess) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return children;
};


const ThemedApp = () => {
  const { primaryColor } = useTheme();
  const { user } = useAuth();
  const [isImpersonating, setIsImpersonating] = useState(() => isImpersonationSessionActive());
  const [endingImpersonation, setEndingImpersonation] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [endNotes, setEndNotes] = useState('');
  const [endNotesError, setEndNotesError] = useState('');
  const location = useLocation();
  const { clearImpersonation } = useImpersonation();
  const impersonationLabel = getImpersonationTargetLabel();

  useEffect(() => {
    setIsImpersonating(isImpersonationSessionActive());
  }, [location.pathname]);

  useEffect(() => {
    const root = document.documentElement;
    if (!isImpersonating) {
      root.classList.remove('is-impersonating');
      return undefined;
    }
    root.classList.add('is-impersonating');
    return () => root.classList.remove('is-impersonating');
  }, [isImpersonating]);

  const impersonationBannerClass = [
    'impersonation-banner',
    isOverviewMobileNavPath(location.pathname) ? 'impersonation-banner--above-mobile-nav' : '',
    location.pathname === '/app/overview/clients' || location.pathname === '/app/overview/company-management'
      ? 'impersonation-banner--above-page-nav'
      : '',
  ].filter(Boolean).join(' ');

  const openEndModal = () => {
    setEndNotes('');
    setEndNotesError('');
    setShowEndModal(true);
  };

  const handleConfirmEndImpersonation = async () => {
    const trimmed = endNotes.trim();
    if (!trimmed) {
      setEndNotesError('Please add a brief description of what you did for this customer.');
      return;
    }

    try {
      setEndingImpersonation(true);
      await clearImpersonation(trimmed);
      // Keep the branded loader up until the admin handoff unloads this page.
    } catch (error) {
      setEndingImpersonation(false);
    } finally {
      setShowEndModal(false);
    }
  };

  return (
    <div style={{ color: primaryColor }}>
      {(endingImpersonation || isImpersonationExiting()) ? (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10050 }}>
          <WorkspaceLoadingScreen message="Returning to admin panel..." />
        </div>
      ) : null}
      {isImpersonating && (
        <div className={impersonationBannerClass}>
          <span>
            {endingImpersonation
              ? 'Ending impersonation session...'
              : impersonationLabel
                ? `You are impersonating ${impersonationLabel}`
                : 'You are currently impersonating a user'}
          </span>
          <button
            type="button"
            className="impersonation-banner__exit"
            onClick={openEndModal}
            disabled={endingImpersonation}
          >
            {endingImpersonation ? 'Exiting...' : 'Exit Impersonation'}
          </button>
        </div>
      )}

      {isImpersonating && showEndModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15,23,42,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => !endingImpersonation && setShowEndModal(false)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: 12,
              maxWidth: 520,
              width: '100%',
              margin: '0 16px',
              padding: '20px 24px 18px',
              boxShadow: '0 18px 40px rgba(15,23,42,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              style={{
                margin: '0 0 8px',
                fontSize: 20,
                fontWeight: 600,
                color: '#111827'
              }}
            >
              End Impersonation Session
            </h2>
            <p
              style={{
                margin: '0 0 16px',
                fontSize: 14,
                color: '#4b5563'
              }}
            >
              Optionally add a short description of what you did to help this customer. This will be saved
              to the impersonation log for future auditing.
            </p>
            <textarea
              value={endNotes}
              onChange={(e) => setEndNotes(e.target.value)}
              placeholder="Example: Reviewed calendar settings, fixed reminder template, verified SMS delivery."
              rows={5}
              style={{
                width: '100%',
                resize: 'vertical',
                padding: '10px 12px',
                borderRadius: 8,
                border: `1px solid ${endNotesError ? '#dc2626' : '#d1d5db'}`,
                fontFamily: 'inherit',
                fontSize: 14,
                boxSizing: 'border-box',
                outline: 'none',
                marginBottom: 8
              }}
            />
            {endNotesError && (
              <div
                style={{
                  color: '#dc2626',
                  fontSize: 12,
                  marginBottom: 10
                }}
              >
                {endNotesError}
              </div>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8
              }}
            >
              <button
                type="button"
                onClick={() => !endingImpersonation && setShowEndModal(false)}
                disabled={endingImpersonation}
                style={{
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  cursor: endingImpersonation ? 'default' : 'pointer',
                  fontSize: 14
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmEndImpersonation}
                disabled={endingImpersonation}
                style={{
                  padding: '8px 18px',
                  borderRadius: 8,
                  border: 'none',
                  backgroundImage: 'linear-gradient(135deg, #4a90e2 0%, #357abd 100%)',
                  color: '#ffffff',
                  cursor: endingImpersonation ? 'default' : 'pointer',
                  fontWeight: 500,
                  fontSize: 14,
                  boxShadow: '0 4px 10px rgba(74,144,226,0.35)',
                  opacity: endingImpersonation ? 0.7 : 1
                }}
              >
                {endingImpersonation ? 'Saving & Exiting...' : 'Save & Exit Impersonation'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <TutorialProvider>
        <CompanyProvider>
          <Routes>
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Main Overview Route */}
            <Route path="/" element={<EmailVerificationRestrictionProvider><Overview /></EmailVerificationRestrictionProvider>}>
              <Route 
                index
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.OVERVIEW.VIEW]}>
                    <UpcomingAppointments />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="today"
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.OVERVIEW.VIEW]}>
                    <OverviewMobileRouteShell />
                  </ProtectedRoute>
                }
              />
              <Route
                path="pending"
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.OVERVIEW.VIEW]}>
                    <OverviewMobileRouteShell />
                  </ProtectedRoute>
                }
              />
              <Route
                path="updates"
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.OVERVIEW.VIEW]}>
                    <OverviewMobileRouteShell />
                  </ProtectedRoute>
                }
              />
              <Route
                path="meetings/today"
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.CALENDAR.VIEW]}>
                    <OverviewMobileRouteShell />
                  </ProtectedRoute>
                }
              />
              <Route
                path="meetings/pending"
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.CALENDAR.VIEW]}>
                    <OverviewMobileRouteShell />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="appointments/:date/:hour" 
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.CALENDAR.VIEW]}>
                    <AppointmentsView />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="company-management" 
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.EMPLOYEES.VIEW]}>
                    <ResponsiveEmployeeManagement />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="company-management/invite"
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.EMPLOYEES.CREATE]}>
                    <InviteEmployeesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="employees/profile/:employeeId"
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.SETTINGS.VIEW]}>
                    <EmployeeProfileRoute />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="appointments"
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.CALENDAR.VIEW]}>
                    <AppointmentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="meetings"
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.CALENDAR.VIEW]}>
                    <MeetingsOverview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="meetings/list"
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.CALENDAR.VIEW]}>
                    <MeetingsPage />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="meeting/:meetingId"
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.CALENDAR.VIEW]}>
                    <MeetingDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="appointment/:appointmentId"
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.CALENDAR.VIEW]}>
                    <StaffAppointmentDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="book" 
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.CALENDAR.VIEW]}>
                    <ManualBookingPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="meetings/new"
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.CALENDAR.VIEW]}>
                    <MeetingCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="book/success" 
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.CALENDAR.VIEW]}>
                    <ManualBookingSuccessPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="clients" 
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.CLIENTS.VIEW]}>
                    <ResponsiveClientManagement />
                  </ProtectedRoute>
                } 
              />
              {/* SMS inbox lives on each client profile; keep old URL working */}
              <Route
                path="messages"
                element={<Navigate to="/app/overview/clients" replace />}
              />
              <Route
                path="clients/profile/:clientId"
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.CLIENTS.VIEW]}>
                    <ClientProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="history" 
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.HISTORY.VIEW]}>
                    <ArchivesPage />
                  </ProtectedRoute>
                } 
              />
              {/* Backward compatibility: redirect old archives path to history */}
              <Route path="archives" element={<Navigate to="/app/overview/history" replace />} />
              <Route path="settings">
                <Route index element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.SETTINGS.VIEW]}>
                    <SettingsWrapper />
                  </ProtectedRoute>
                } />
                <Route path="locations/new" element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.SETTINGS.VIEW]}>
                    <SettingsWrapper />
                  </ProtectedRoute>
                } />
                <Route path="locations/:locationId/edit" element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.SETTINGS.VIEW]}>
                    <SettingsWrapper />
                  </ProtectedRoute>
                } />
                <Route path=":section" element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.SETTINGS.VIEW]}>
                    <SettingsWrapper />
                  </ProtectedRoute>
                } />
              </Route>
              <Route
                path="services/new"
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.SETTINGS.VIEW]}>
                    <ServiceCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="services/:serviceId/edit"
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.SETTINGS.VIEW]}>
                    <ServiceEditPage />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="services/:serviceId" 
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.SETTINGS.VIEW]}>
                    <ServiceDetailsPage />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="services/:serviceId/packages/:packageId"
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.SETTINGS.VIEW]}>
                    <PackageDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="services/:serviceId/addons/:addOnId"
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.SETTINGS.VIEW]}>
                    <AddOnDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="locations/new"
                element={<Navigate to="/app/overview/settings/locations/new" replace />}
              />
              <Route
                path="locations/:locationId/edit"
                element={<LegacyLocationEditRedirect />}
              />
              <Route
                path="calendar/v3"
                element={<Navigate to="/app/overview/calendar" replace />}
              />
              <Route
                path="calendar"
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.CALENDAR.VIEW]}>
                    <TeamLabelCalendarPage />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="support" 
                element={
                  <SupportPage />
                } 
              />
              <Route
                path="support/tickets"
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.SUPPORT.VIEW]}>
                    <SupportTicketsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="support/tickets/:ticketId"
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.SUPPORT.VIEW]}>
                    <SupportTicketDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="dev/tools"
                element={
                  <SpecialAdminRoute>
                    <DevToolsPage />
                  </SpecialAdminRoute>
                }
              />
              <Route 
                path="permissions" 
                element={
                  <ProtectedRoute requiredPermissions={[PERMISSIONS.SETTINGS.EDIT]}>
                    <PermissionsManager />
                  </ProtectedRoute>
                } 
              />
            </Route>

            <Route 
              path="/appointment/:companySlug/:appointmentId" 
              element={<AppointmentDetailsPage />}
            />

            {/* Redirect unknown app routes to overview index */}
            <Route path="*" element={<Navigate to="/app/overview" replace />} />
          </Routes>
        </CompanyProvider>
      </TutorialProvider>
    </div>
  );
};

export default ThemedApp;

